import React, { useState, useEffect } from 'react';
import { Tab, Tabs, Alert } from 'react-bootstrap';
import { FlaskConical, Info } from 'lucide-react';
import useAuthHeaders from '../hooks/useAuthHeaders';
import CredentialForm from './playground/CredentialForm';
import { exportEncryptedFiel } from '../utils/fielCrypto';
import { apiFetch, readApiError } from '../utils/apiError';
import ConsultaFacturasTab from './playground/ConsultaFacturasTab';
import OpinionCumplimientoTab from './playground/OpinionCumplimientoTab';
import CsfTab from './playground/CsfTab';
import DeclaracionesTab from './playground/DeclaracionesTab';
import RepseTab from './playground/RepseTab';
import InformacionFiscalTab from './playground/InformacionFiscalTab';
import ImssOcTab from './playground/ImssOcTab';
import ComunicadosTab from './playground/ComunicadosTab';
import NotificacionesTab from './playground/NotificacionesTab';
import './Playground.css';

const API_URL = process.env.REACT_APP_API_URL;

const Playground = () => {
  const { userId, getToken } = useAuthHeaders();

  const [authMode, setAuthMode] = useState('fiel');

  const [rfc, setRfc]                     = useState('');
  const [rfcManualMode, setRfcManualMode]   = useState(false);
  const [rfcManualInput, setRfcManualInput] = useState('');

  const [privateKeyFile, setPrivateKeyFile] = useState(null);
  const [certFile, setCertFile]             = useState(null);
  const [fielPassword, setFielPassword]     = useState('');

  const [secret, setSecret] = useState('');

  const [rfcOptions, setRfcOptions] = useState([]);
  const [rfcLoading, setRfcLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('facturas');

  // ── Signing key vault state ────────────────────────────
  const [signingKeyStatus, setSigningKeyStatus]   = useState(null);
  const [signingKeyLoading, setSigningKeyLoading] = useState(false);
  const [saveToVault, setSaveToVault]             = useState(false);
  const [encryptFiel, setEncryptFiel]             = useState(true); // recomendado: cifrar e.firma en el navegador
  const [lastVaultRequest, setLastVaultRequest]   = useState(null);

  // Fetch RFC guardados del usuario
  useEffect(() => {
    if (!userId) return;
    const fetchRfcs = async () => {
      setRfcLoading(true);
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/UsersRFC/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRfcOptions((Array.isArray(data) ? data : []).filter(r => !r.isDeleted));
        }
      } catch { } finally {
        setRfcLoading(false);
      }
    };
    fetchRfcs();
  }, [userId, getToken]);

  // Consultar estado del proveedor de firma cuando cambia el RFC en modo FIEL
  useEffect(() => {
    if (!rfc || authMode !== 'fiel') {
      setSigningKeyStatus(null);
      setSaveToVault(false);
      setLastVaultRequest(null);
      return;
    }
    let cancelled = false;
    const fetchStatus = async () => {
      setSigningKeyLoading(true);
      try {
        const token = await getToken();
        const res = await fetch(
          `${API_URL}/SecretProvider/signing-key/${encodeURIComponent(rfc)}/status`,
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        if (!cancelled) {
          setSigningKeyStatus(res.ok ? await res.json() : null);
        }
      } catch {
        if (!cancelled) setSigningKeyStatus(null);
      } finally {
        if (!cancelled) setSigningKeyLoading(false);
      }
    };
    fetchStatus();
    return () => { cancelled = true; };
  }, [rfc, authMode, getToken]);

  // Eliminar FIEL del vault
  const removeSigningKey = async () => {
    try {
      const token = await getToken();
      const res = await apiFetch(
        `${API_URL}/SecretProvider/signing-key/${encodeURIComponent(rfc)}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (!res.ok) return { success: false, error: await readApiError(res) };
      setSigningKeyStatus({ isRegistered: false });
      setLastVaultRequest({
        method: 'DELETE',
        baseUrl: `${API_URL}/SecretProvider/signing-key/${rfc}`,
        queryParams: null,
        headers: {},
        jsonBody: null,
        jsPrefix: null,
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Guardar FIEL (.cer + .key) en el vault gestionado por SatGo
  const storeSigningKey = async () => {
    if (!certFile || !privateKeyFile)
      return { success: false, error: 'Faltan los archivos FIEL (.cer y .key).' };
    try {
      const token = await getToken();
      const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const [cerBase64, keyBase64] = await Promise.all([
        toBase64(certFile),
        toBase64(privateKeyFile),
      ]);
      const res = await apiFetch(`${API_URL}/SecretProvider/signing-key/store`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rfc,
          cerFileBase64: cerBase64,
          keyFileBase64: keyBase64,
          password: fielPassword || null,
        }),
      });
      if (!res.ok) return { success: false, error: await readApiError(res) };
      const data = await res.json();
      setSigningKeyStatus({ isRegistered: true, providerType: data.providerType, keyName: data.certName });
      setSaveToVault(false);
      setLastVaultRequest({
        method: 'POST',
        baseUrl: `${API_URL}/SecretProvider/signing-key/store`,
        queryParams: null,
        headers: {},
        jsonBody: {
          rfc,
          cerFileBase64: '<base64 del archivo .cer>',
          keyFileBase64: '<base64 del archivo .key>',
          password: '{contraseña_fiel}',
        },
        jsPrefix:
`// Convertir archivos a base64
const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload  = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const [cerBase64, keyBase64] = await Promise.all([
  toBase64(cerFile),  // File — tu archivo .cer
  toBase64(keyFile),  // File — tu archivo .key
]);`,
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Cifrar la llave y la contraseña, y descargar el .key.jwe para usar en un sistema propio
  const exportEncryptedCredentials = async () => {
    if (!privateKeyFile || !fielPassword)
      return { success: false, error: 'Faltan la llave privada (.key) y/o la contraseña.' };
    try {
      const token = await getToken();
      const { passJwe } = await exportEncryptedFiel({ rfc, privateKeyFile, fielPassword }, token);
      return { success: true, passwordJwe: passJwe };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // ── Validación de credenciales ─────────────────────────
  const isValid = authMode === 'fiel'
    ? signingKeyStatus?.isRegistered
      ? !!rfc
      : !!(rfc && privateKeyFile && certFile && fielPassword)
    : !!(rfc && secret);

  const credentials = {
    authMode,
    rfc,
    privateKeyFile,
    certFile,
    fielPassword,
    secret,
    signingKeyStatus,
    saveToVault,
    encryptFiel,
    onStoreSigningKey: storeSigningKey,
  };

  return (
    <div className="playground-container">

      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">
            <FlaskConical size={28} className="me-2" style={{ color: '#13a0ff' }} />
            API Tester
          </h2>
          <p className="page-subtitle">
            Prueba los endpoints con tu sesión activa — FIEL o CIEC
          </p>
        </div>
      </div>

      <Alert variant="info" className="d-flex align-items-start gap-2 mb-4 py-3">
        <Info size={18} className="flex-shrink-0 mt-1" />
        <div>
          <strong>Tu token de sesión se gestiona automáticamente.</strong>
          {' '}Selecciona el método de autenticación y completa las credenciales.
          Los resultados se muestran en pantalla o se descargan como PDF, igual que en producción.
        </div>
      </Alert>

      <CredentialForm
        authMode={authMode}
        setAuthMode={setAuthMode}
        rfc={rfc}
        setRfc={setRfc}
        rfcManualMode={rfcManualMode}
        setRfcManualMode={setRfcManualMode}
        rfcManualInput={rfcManualInput}
        setRfcManualInput={setRfcManualInput}
        rfcOptions={rfcOptions}
        rfcLoading={rfcLoading}
        privateKeyFile={privateKeyFile}
        setPrivateKeyFile={setPrivateKeyFile}
        certFile={certFile}
        setCertFile={setCertFile}
        fielPassword={fielPassword}
        setFielPassword={setFielPassword}
        secret={secret}
        setSecret={setSecret}
        signingKeyStatus={signingKeyStatus}
        signingKeyLoading={signingKeyLoading}
        saveToVault={saveToVault}
        setSaveToVault={setSaveToVault}
        encryptFiel={encryptFiel}
        setEncryptFiel={setEncryptFiel}
        onRemoveSigningKey={removeSigningKey}
        onExportEncryptedFiel={exportEncryptedCredentials}
        lastVaultRequest={lastVaultRequest}
      />

      <div className="playground-tabs">
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-0">
          <Tab eventKey="facturas" title="📄 Facturas CFDI">
            <ConsultaFacturasTab credentials={credentials} isValid={isValid} />
          </Tab>
          <Tab eventKey="imssoc" title="🏥 OC IMSS">
            <ImssOcTab credentials={credentials} />
          </Tab>
          <Tab eventKey="oc" title="✅ Opinión de Cumplimiento">
            <OpinionCumplimientoTab credentials={credentials} isValid={isValid} />
          </Tab>
          <Tab eventKey="csf" title="📋 Constancia Fiscal">
            <CsfTab credentials={credentials} isValid={isValid} />
          </Tab>
          <Tab eventKey="dec" title="🗂️ Declaraciones">
            <DeclaracionesTab credentials={credentials} isValid={isValid} />
          </Tab>
          <Tab eventKey="comunicados" title="📨 Comunicados">
            <ComunicadosTab credentials={credentials} isValid={isValid} />
          </Tab>
          <Tab eventKey="notificaciones" title="🔔 Notificaciones">
            <NotificacionesTab credentials={credentials} isValid={isValid} />
          </Tab>
          <Tab eventKey="repse" title="🏭 REPSE">
            <RepseTab />
          </Tab>
          <Tab eventKey="infofiscal" title="🏛️ Info Fiscal">
            <InformacionFiscalTab credentials={credentials} isValid={isValid} />
          </Tab>
        </Tabs>
      </div>

    </div>
  );
};

export default Playground;
